import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { EncoderWSSEvent, EncoderSampleData, FlappingAlertPayload, FlappingResolvedPayload } from '@shared/types';
import { FLAPPING_CONFIG, isDuplicateEvent } from '@shared/types';

// localStorage persistence for encoder online/offline status
const STORAGE_KEY = 'admin-portal:encoder-wss-status';
const STATUS_TTL_MS = 10 * 60 * 1000; // 10 minutes - stale data is discarded

interface PersistedEncoderStatus {
  encoderID: string;
  simpleName: string;
  clientID: string;
  online: boolean;
  streamInfo: EncoderSampleData | null;
  lastUpdated: number;
}

function loadPersistedStatus(): Map<string, PersistedEncoderStatus> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Map();

    const parsed: PersistedEncoderStatus[] = JSON.parse(stored);
    const now = Date.now();
    const result = new Map<string, PersistedEncoderStatus>();

    // Filter out stale entries
    for (const entry of parsed) {
      if (now - entry.lastUpdated < STATUS_TTL_MS) {
        result.set(entry.encoderID, entry);
      }
    }

    return result;
  } catch {
    return new Map();
  }
}

function savePersistedStatus(encoders: Map<string, EncoderWSSRecord>): void {
  try {
    const toSave: PersistedEncoderStatus[] = [];
    for (const record of encoders.values()) {
      toSave.push({
        encoderID: record.encoderID,
        simpleName: record.simpleName,
        clientID: record.clientID,
        online: record.online,
        streamInfo: record.streamInfo,
        lastUpdated: record.lastUpdated,
      });
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    // Ignore localStorage errors (quota, private browsing, etc.)
  }
}

/**
 * Encoder WSS Store
 *
 * Tracks real-time encoder connect/disconnect events from Scaledrone WebSocket.
 * Detects "flapping" (rapid connect/disconnect cycles) to surface encoder instability.
 */

/**
 * State for a single encoder's WSS activity
 */
export interface EncoderWSSRecord {
  encoderID: string;
  simpleName: string;
  clientID: string;
  online: boolean;
  streamInfo: EncoderSampleData | null;
  /** Recent connect/disconnect events within the flapping window */
  events: Array<{ type: 'connect' | 'disconnect'; timestamp: number }>;
  /** Current state based on flapping detection */
  state: 'normal' | 'warning' | 'alert';
  /** True if encoder is attached to a live stream (for false positive detection) */
  attachedToLiveStream: boolean;
  /** Timestamp of last event */
  lastUpdated: number;
}

interface EncoderWSSState {
  /** All tracked encoders keyed by encoderID */
  encoders: Map<string, EncoderWSSRecord>;

  // Actions
  processEvent: (event: EncoderWSSEvent) => 'normal' | 'warning' | 'alert';
  setAttachedToLiveStream: (encoderID: string, attached: boolean) => void;
  clearEncoder: (encoderID: string) => void;

  // Server sync actions
  setFlappingFromServer: (alerts: FlappingAlertPayload[]) => void;
  handleServerFlapping: (alert: FlappingAlertPayload) => void;
  handleServerFlappingResolved: (resolved: FlappingResolvedPayload) => void;

  // Selectors
  getEncodersWithIssues: () => EncoderWSSRecord[];
  getEncoder: (encoderID: string) => EncoderWSSRecord | undefined;
}

// Initialize encoders map from localStorage
function initializeEncoders(): Map<string, EncoderWSSRecord> {
  const persisted = loadPersistedStatus();
  const encoders = new Map<string, EncoderWSSRecord>();

  for (const [id, status] of persisted) {
    encoders.set(id, {
      encoderID: status.encoderID,
      simpleName: status.simpleName,
      clientID: status.clientID,
      online: status.online,
      streamInfo: status.streamInfo,
      events: [], // Don't persist events - they're time-sensitive
      state: 'normal', // Reset state - will be updated by server sync
      attachedToLiveStream: false,
      lastUpdated: status.lastUpdated,
    });
  }

  return encoders;
}

export const useEncoderWSSStore = create<EncoderWSSState>()(
  devtools(
    (set, get) => ({
      encoders: initializeEncoders(),

      processEvent: (event) => {
        let newState: 'normal' | 'warning' | 'alert' = 'normal';

        set((state) => {
          const encoders = new Map(state.encoders);
          const existing = encoders.get(event.encoderID);
          const now = event.timestamp;
          const lastEvent = existing?.events[existing.events.length - 1];

          // Deduplication: skip if same event type within DEDUP_WINDOW_MS
          if (isDuplicateEvent(event.eventType, now, lastEvent)) {
            return state;
          }

          // Build event history
          let events = existing?.events || [];
          events = [...events, { type: event.eventType, timestamp: now }];

          // Prune events outside the DISPLAY window (10 min) - keep for UI/history
          const displayCutoff = now - FLAPPING_CONFIG.RESOLVE_AFTER_MS;
          events = events.filter((e) => e.timestamp > displayCutoff);

          // Count cycles only from events within the DETECTION window (60s)
          const detectionCutoff = now - FLAPPING_CONFIG.WINDOW_MS;
          const recentEvents = events.filter((e) => e.timestamp > detectionCutoff);
          let cycles = 0;
          for (let i = 0; i < recentEvents.length - 1; i++) {
            if (recentEvents[i].type === 'disconnect' && recentEvents[i + 1].type === 'connect') {
              cycles++;
            }
          }

          // Determine state based on recent cycles
          if (cycles >= FLAPPING_CONFIG.ALERT_CYCLES) {
            newState = 'alert';
          } else if (cycles >= FLAPPING_CONFIG.WARNING_CYCLES) {
            newState = 'warning';
          }

          // Preserve existing state if we're still within the 10-minute window
          // This prevents the alert from disappearing when new events come in after 60s
          const previousState = existing?.state;
          const previousLastUpdated = existing?.lastUpdated;
          if (newState === 'normal' && previousState && previousState !== 'normal' && previousLastUpdated) {
            const timeSinceLastUpdate = now - previousLastUpdated;
            if (timeSinceLastUpdate < FLAPPING_CONFIG.RESOLVE_AFTER_MS) {
              // Keep the existing state - still within 10-minute window
              newState = previousState;
            }
          }

          // Check if attached to live stream (preserve existing value or use false)
          const attachedToLiveStream = existing?.attachedToLiveStream || false;

          // If attached to live stream and just disconnected with at least 1 flapping cycle,
          // show warning. Require cycles >= 1 to avoid false positives from single transient disconnects.
          if (attachedToLiveStream && event.eventType === 'disconnect' && newState === 'normal' && cycles >= 1) {
            newState = 'warning';
          }

          encoders.set(event.encoderID, {
            encoderID: event.encoderID,
            simpleName: event.simpleName,
            clientID: event.clientID,
            online: event.online,
            streamInfo: event.streamInfo,
            events,
            state: newState,
            attachedToLiveStream,
            lastUpdated: now,
          });

          // Persist to localStorage
          savePersistedStatus(encoders);

          return { encoders };
        });

        return newState;
      },

      setAttachedToLiveStream: (encoderID, attached) =>
        set((state) => {
          const encoders = new Map(state.encoders);
          const existing = encoders.get(encoderID);
          if (existing) {
            encoders.set(encoderID, { ...existing, attachedToLiveStream: attached });
          }
          return { encoders };
        }),

      clearEncoder: (encoderID) =>
        set((state) => {
          const encoders = new Map(state.encoders);
          encoders.delete(encoderID);
          return { encoders };
        }),

      // Server sync: Set initial flapping state from server on page load
      setFlappingFromServer: (alerts) =>
        set((state) => {
          const encoders = new Map(state.encoders);

          for (const alert of alerts) {
            const existing = encoders.get(alert.encoderId);

            // Merge server state with existing client state
            encoders.set(alert.encoderId, {
              encoderID: alert.encoderId,
              simpleName: alert.simpleName,
              clientID: alert.clientId,
              online: alert.online,
              streamInfo: existing?.streamInfo ?? null,
              events: alert.events,
              state: alert.severity,
              attachedToLiveStream: existing?.attachedToLiveStream ?? false,
              lastUpdated: alert.detectedAt,
            });
          }

          // Persist to localStorage
          savePersistedStatus(encoders);

          return { encoders };
        }),

      // Server sync: Handle incoming flapping alert from SSE
      handleServerFlapping: (alert) =>
        set((state) => {
          const encoders = new Map(state.encoders);
          const existing = encoders.get(alert.encoderId);

          encoders.set(alert.encoderId, {
            encoderID: alert.encoderId,
            simpleName: alert.simpleName,
            clientID: alert.clientId,
            online: alert.online,
            streamInfo: existing?.streamInfo ?? null,
            events: alert.events,
            state: alert.severity,
            attachedToLiveStream: existing?.attachedToLiveStream ?? false,
            lastUpdated: Date.now(),
          });

          // Persist to localStorage
          savePersistedStatus(encoders);

          return { encoders };
        }),

      // Server sync: Handle flapping resolved from SSE
      handleServerFlappingResolved: (resolved) =>
        set((state) => {
          const encoders = new Map(state.encoders);
          const existing = encoders.get(resolved.encoderId);

          if (existing) {
            // Clear the flapping state but keep the encoder record
            encoders.set(resolved.encoderId, {
              ...existing,
              state: 'normal',
              events: [],
              lastUpdated: Date.now(),
            });

            // Persist to localStorage
            savePersistedStatus(encoders);
          }

          return { encoders };
        }),

      getEncodersWithIssues: () => {
        const { encoders } = get();
        return Array.from(encoders.values())
          .filter((e) => e.state !== 'normal')
          .sort((a, b) => {
            // Alert first, then warning
            if (a.state === 'alert' && b.state !== 'alert') return -1;
            if (b.state === 'alert' && a.state !== 'alert') return 1;
            // Then by encoderID for stable ordering (prevents jumping on updates)
            return a.encoderID.localeCompare(b.encoderID);
          });
      },

      getEncoder: (encoderID) => get().encoders.get(encoderID),
    }),
    { name: 'encoder-wss-store' }
  )
);
